import { useState } from "react";

/**
 * The slice of `localStorage` this hook uses. Narrowed to two methods so the
 * fallback behaviour can be tested against a store that throws, which is the
 * case that matters and the one a real browser only produces under settings
 * nobody develops with.
 */
export interface CollapsedStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const COLLAPSED = "1";
const EXPANDED = "0";

/**
 * Read a remembered collapsed state, falling back to `fallback` for anything
 * unexpected.
 *
 * **Every failure resolves to `fallback`, never a throw.** A browser with site
 * data blocked throws on the getter itself, and a section that refuses to
 * render because it could not remember whether it was folded would be a far
 * worse bug than forgetting.
 */
export function readCollapsed(store: CollapsedStore, key: string, fallback: boolean): boolean {
  try {
    const value = store.getItem(key);
    if (value === COLLAPSED) return true;
    if (value === EXPANDED) return false;
    return fallback;
  } catch {
    return fallback;
  }
}

/** Best-effort write. Same reasoning as the read: losing the preference is not worth an error. */
export function writeCollapsed(store: CollapsedStore, key: string, collapsed: boolean): void {
  try {
    store.setItem(key, collapsed ? COLLAPSED : EXPANDED);
  } catch {
    // Storage full, or blocked. Nothing to do and nothing worth saying.
  }
}

/**
 * Whether one collapsible section is folded, remembered per browser.
 *
 * A section's own key, rather than the `Record<string, boolean>` under a single
 * key that `Sidebar` uses for its nav groups — that shape earns its keep when
 * the set of sections is dynamic, and here there is exactly one.
 *
 * Deliberately NOT shared with the server or a user setting: this is a
 * per-device reading preference, the same tier as the list/card choice in
 * `useViewMode`.
 */
export function useCollapsed(storageKey: string, defaultCollapsed = false): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() =>
    typeof window === "undefined"
      ? defaultCollapsed
      : readCollapsed(window.localStorage, storageKey, defaultCollapsed),
  );

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      if (typeof window !== "undefined") writeCollapsed(window.localStorage, storageKey, next);
      return next;
    });
  };

  return [collapsed, toggle];
}
